/**
 * GFS 双通道风场解码器
 * 将 R=U_symlog, G=V_symlog 编码的PNG图片解码为U/V风速分量
 *
 * 编码规则:
 *   - 图片尺寸: 282×281 (第0行=元数据, 第1-281行=数据)
 *   - 第0行存储元数据:
 *       pixel[0]: R=uMin高8位, G=uMax高8位, B=vMin高8位
 *       pixel[1]: R=uMin低8位, G=uMax低8位, B=vMin低8位
 *       pixel[2]: R=vMax高8位, G=vMax低8位
 *   - 数据行: R通道=U分量, G通道=V分量 (symlog编码)
 *
 * 使用示例:
 *   import WindDecoder from '@/utils/windDecoder'
 *
 *   const decoder = new WindDecoder()
 *   const result = await decoder.decode(url)
 *   // result = { uData: Float32Array, vData: Float32Array, meta: {...}, stats: {...} }
 */

const STORE_MIN = -400.0
const STORE_MAX = 2000.0
const GRID_ROWS = 281
const GRID_COLS = 281

/**
 * Symlog 解码函数
 * @param {number} gray - 灰度值 (0-255)
 * @param {number} absMax - 绝对最大值
 * @param {number} logAbsMax - log(1 + absMax)
 * @returns {number} 解码后的物理值
 */
function symlogDecode(gray, absMax, logAbsMax) {
  if (gray <= 127.0) {
    const ratio = (127.0 - gray) / 127.0
    return -(Math.exp(ratio * logAbsMax) - 1.0)
  } else {
    const ratio = (gray - 127.0) / 128.0
    return Math.exp(ratio * logAbsMax) - 1.0
  }
}

class WindDecoder {
  constructor() {
    this.uData = null // Float32Array[GRID_ROWS * GRID_COLS]
    this.vData = null // Float32Array[GRID_ROWS * GRID_COLS]
    this.meta = null   // { uMin, uMax, vMin, vMax, uAbsMax, vAbsMax, uLogAbsMax, vLogAbsMax }
    this.uStats = null // { min, max, avg }
    this.vStats = null // { min, max, avg }
    this.rawRGB = null // { r, g, b, width, height }
  }

  /**
   * 从URL加载并解码图片
   * @param {string} url - 图片地址
   * @returns {Promise<{uData: Float32Array, vData: Float32Array, meta: Object, uStats: Object, vStats: Object}>}
   */
  decodeFromUrl(url) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        try {
          const result = this.decodeFromImage(img)
          resolve(result)
        } catch (err) {
          reject(err)
        }
      }
      img.onerror = () => reject(new Error('图片加载失败: ' + url))
      img.src = url
    })
  }

  /**
   * 从Image对象解码
   * @param {HTMLImageElement} img
   * @returns {{uData: Float32Array, vData: Float32Array, meta: Object, uStats: Object, vStats: Object}}
   */
  decodeFromImage(img) {
    const w = img.width
    const h = img.height

    if (w !== GRID_COLS || h !== GRID_ROWS + 1) {
      throw new Error(`图片尺寸错误，应为 ${GRID_COLS}×${GRID_ROWS + 1}，实际: ${w}×${h}`)
    }

    // 画到canvas读取RGB
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0)
    const imgData = ctx.getImageData(0, 0, w, h)
    const pixels = imgData.data

    // 提取RGB数组
    const rArr = new Uint8Array(w * h)
    const gArr = new Uint8Array(w * h)
    const bArr = new Uint8Array(w * h)
    for (let i = 0; i < w * h; i++) {
      rArr[i] = pixels[i * 4]
      gArr[i] = pixels[i * 4 + 1]
      bArr[i] = pixels[i * 4 + 2]
    }
    this.rawRGB = { r: rArr, g: gArr, b: bArr, width: w, height: h }

    // 解析第0行元数据
    const uMinEnc = (rArr[0] << 8) | rArr[1]
    const uMaxEnc = (gArr[0] << 8) | gArr[1]
    const vMinEnc = (bArr[0] << 8) | bArr[1]
    const vMaxEnc = (rArr[2] << 8) | gArr[2]

    const uMin = uMinEnc / 65535.0 * (STORE_MAX - STORE_MIN) + STORE_MIN
    const uMax = uMaxEnc / 65535.0 * (STORE_MAX - STORE_MIN) + STORE_MIN
    const vMin = vMinEnc / 65535.0 * (STORE_MAX - STORE_MIN) + STORE_MIN
    const vMax = vMaxEnc / 65535.0 * (STORE_MAX - STORE_MIN) + STORE_MIN

    const uAbsMax = Math.max(Math.abs(uMin), Math.abs(uMax))
    const uLogAbsMax = Math.log(1.0 + uAbsMax)
    const vAbsMax = Math.max(Math.abs(vMin), Math.abs(vMax))
    const vLogAbsMax = Math.log(1.0 + vAbsMax)

    this.meta = { uMin, uMax, vMin, vMax, uAbsMax, vAbsMax, uLogAbsMax, vLogAbsMax }

    // 解码数据
    this.uData = new Float32Array(GRID_ROWS * GRID_COLS)
    this.vData = new Float32Array(GRID_ROWS * GRID_COLS)

    const uStats = { min: Infinity, max: -Infinity, sum: 0, cnt: 0 }
    const vStats = { min: Infinity, max: -Infinity, sum: 0, cnt: 0 }

    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const idx = (row + 1) * GRID_COLS + col
        const dataIdx = row * GRID_COLS + col

        const uGray = rArr[idx]
        const vGray = gArr[idx]

        const uVal = symlogDecode(uGray, uAbsMax, uLogAbsMax)
        const vVal = symlogDecode(vGray, vAbsMax, vLogAbsMax)

        this.uData[dataIdx] = uVal
        this.vData[dataIdx] = vVal

        if (isFinite(uVal)) {
          if (uVal < uStats.min) uStats.min = uVal
          if (uVal > uStats.max) uStats.max = uVal
          uStats.sum += uVal
          uStats.cnt++
        }
        if (isFinite(vVal)) {
          if (vVal < vStats.min) vStats.min = vVal
          if (vVal > vStats.max) vStats.max = vVal
          vStats.sum += vVal
          vStats.cnt++
        }
      }
    }

    this.uStats = uStats
    this.vStats = vStats

    return {
      uData: this.uData,
      vData: this.vData,
      meta: this.meta,
      uStats: this.uStats,
      vStats: this.vStats
    }
  }

  /**
   * 获取指定格点的风速分量
   * @param {number} row - 行号 (0=70°N, 从北到南)
   * @param {number} col - 列号 (0=0°E, 从西到东)
   * @returns {{u: number, v: number, speed: number, direction: number}}
   */
  getWindAt(row, col) {
    if (row < 0 || row >= GRID_ROWS || col < 0 || col >= GRID_COLS) {
      throw new Error(`坐标超出范围 [0~${GRID_ROWS - 1}, 0~${GRID_COLS - 1}]`)
    }
    if (!this.uData || !this.vData) {
      throw new Error('尚未解码图片，请先调用 decodeFromUrl 或 decodeFromImage')
    }
    const idx = row * GRID_COLS + col
    const u = this.uData[idx]
    const v = this.vData[idx]
    const speed = Math.sqrt(u * u + v * v)
    const direction = (Math.atan2(v, u) * 180) / Math.PI // v正值=北向, u正值=东向
    return { u, v, speed, direction }
  }

  /**
   * 获取所有格点的风速大小
   * @returns {Float32Array} 风速大小数组
   */
  getAllSpeed() {
    if (!this.uData || !this.vData) {
      throw new Error('尚未解码图片，请先调用 decodeFromUrl 或 decodeFromImage')
    }
    const speed = new Float32Array(GRID_ROWS * GRID_COLS)
    for (let i = 0; i < speed.length; i++) {
      speed[i] = Math.sqrt(this.uData[i] * this.uData[i] + this.vData[i] * this.vData[i])
    }
    return speed
  }

  /**
   * 打印所有格点数据到控制台
   * @param {number} [sampleStep=20] - 采样步长，只打印每隔sampleStep行/列的数据
   */
  printData(sampleStep = 20) {
    if (!this.uData || !this.vData) {
      console.warn('尚未解码图片')
      return
    }

    console.group('=== 风场解码数据 ===')
    console.log('U 通道:', 'min=' + this.uStats.min.toFixed(4), 'max=' + this.uStats.max.toFixed(4), 'avg=' + (this.uStats.sum / this.uStats.cnt).toFixed(4))
    console.log('V 通道:', 'min=' + this.vStats.min.toFixed(4), 'max=' + this.vStats.max.toFixed(4), 'avg=' + (this.vStats.sum / this.vStats.cnt).toFixed(4))

    console.log('\n格点数据 (行=纬度, 列=经度, 采样步长=' + sampleStep + '):')
    console.log('行\\列 | U(东西向) / V(南北向)')

    let header = '     '
    for (let c = 0; c < GRID_COLS; c += sampleStep) {
      header += String(c).padStart(8)
    }
    console.log(header)

    for (let r = 0; r < GRID_ROWS; r += sampleStep) {
      let line = String(r).padStart(4) + ' '
      for (let c = 0; c < GRID_COLS; c += sampleStep) {
        const idx = r * GRID_COLS + c
        const u = this.uData[idx].toFixed(1)
        const v = this.vData[idx].toFixed(1)
        line += u + '/' + v + ' '
      }
      console.log(line)
    }
    console.groupEnd()
  }

  /**
   * 获取解码结果摘要
   * @returns {string}
   */
  getSummary() {
    if (!this.meta || !this.uStats || !this.vStats) return '尚未解码'
    return [
      `U: [${this.uStats.min.toFixed(2)}, ${this.uStats.max.toFixed(2)}] avg=${(this.uStats.sum / this.uStats.cnt).toFixed(2)}`,
      `V: [${this.vStats.min.toFixed(2)}, ${this.vStats.max.toFixed(2)}] avg=${(this.vStats.sum / this.vStats.cnt).toFixed(2)}`
    ].join('\n')
  }
}

export default WindDecoder

export { STORE_MIN, STORE_MAX, GRID_ROWS, GRID_COLS }
